import { isApiProblem, problemMessage } from "../api/problem";

/**
 * A failed query load, in the one shape used everywhere a widget's data failed to fetch (UX findings 5 + 6,
 * 2026-09-17 review). Before this, each screen hand-rolled its own version of "could not be loaded" and drifted:
 * some had a working Retry, some had none for the identical failure, and one (the service desk queue when
 * `ServiceDeskQueue` is off) had Retry on a state retrying can never fix.
 *
 * Two rules, applied consistently:
 *  - **Retry only when a retry could plausibly help.** A feature switched off or a permission the caller
 *    doesn't hold (`isFeatureDisabled`, or 403) can never be fixed by clicking Retry — offering it just teaches
 *    people to press a button that never works. Every other failure (network, 5xx, an endpoint that 404s
 *    because it isn't built yet) gets Retry.
 *  - **A hard failure never reads like the calm "this is off for now" banners it often sits next to.** It keeps
 *    the existing red `msg` alert treatment (not `msg info`) and adds a `!` marker so the difference isn't
 *    colour alone — someone skimming in grayscale, or just moving fast, can still tell "broken" from "expected".
 */
export function LoadError({ error, onRetry, lead }: { error: unknown; onRetry?: () => void; lead?: string }) {
  const canRetry = onRetry !== undefined && !(isApiProblem(error) && (error.isFeatureDisabled || error.status === 403));
  return (
    <div className="msg gf-load-error" role="alert">
      <span aria-hidden="true">! </span>
      {lead ? `${lead} ` : ""}
      {problemMessage(error)}
      {canRetry && (
        <>
          {" "}
          <button type="button" className="linkish" onClick={onRetry}>
            Retry
          </button>
        </>
      )}
    </div>
  );
}
